// ChoroplethMap.jsx
import React, { useMemo, useRef, useEffect, useState } from "react";
import Papa from "papaparse";
import { MapContainer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* =========================
   Fixed bins & color scale
   ========================= */
const FIXED_COLORS = [
  "#f7fbff", // 0–10
  "#B7F4FF", // 11–20
  "#84E7FF", // 21–30
  "#A0EDFF", // 31–40
  "#51DCFF", // 41–50
  "#02CDFF", // 51–60
  "#2EB4F2", // 61–70
  "#2899E8", // 71–80
  "#144EC3", // 81–90
  "#010099", // 91–100
];
const FIXED_BREAKS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

/* Map fit helper */
function FitToGeoJSON({
  geojson,
  maxZoom = 12,
  minZoomFixed = null,
  boundsPadding = 0.15,
  lockDragToData = true,
  maxBoundsViscosity = 1.0,
  zoomOutFactor = 1.25,
}) {
  const map = useMap();

  useEffect(() => {
    if (!geojson) return;

    const layer = L.geoJSON(geojson);
    const bounds = layer.getBounds();
    if (!bounds || !bounds.isValid()) return;

    map.fitBounds(bounds, { padding: [20, 20] });

    const currentZoom = map.getZoom();
    const adjustedZoom = currentZoom - Math.log2(zoomOutFactor);
    map.setZoom(adjustedZoom);

    if (lockDragToData) {
      const padded = bounds.pad(boundsPadding);
      map.setMaxBounds(padded);
      map.options.maxBounds = padded;
      map.options.maxBoundsViscosity = maxBoundsViscosity;
    } else {
      map.setMaxBounds(null);
      map.options.maxBounds = null;
    }

    const bestZoom = map.getBoundsZoom(bounds, true);
    map.setMaxZoom(maxZoom);
    const computedMin =
      Number.isFinite(minZoomFixed) && minZoomFixed !== null
        ? minZoomFixed
        : bestZoom - Math.log2(zoomOutFactor);
    map.setMinZoom(computedMin);
  }, [
    geojson,
    map,
    maxZoom,
    minZoomFixed,
    boundsPadding,
    lockDragToData,
    maxBoundsViscosity,
    zoomOutFactor,
  ]);

  return null;
}

/* Legend */
function Legend({ breaks, colors, labels }) {
  if (!breaks?.length || !colors?.length) return null;

  const autoLabels =
    labels ||
    Array.from({ length: colors.length }, (_, i) => {
      const lo = breaks[i];
      const hi = breaks[i + 1];
      return i === 0 ? `${lo}–${hi}` : `${lo + 1}–${hi}`;
    });

  return (
    <div
      style={{
        position: "absolute",
        right: 10,
        bottom: 10,
        zIndex: 1000,
        background: "white",
        padding: "8px 10px",
        border: "1px solid #ccc",
        borderRadius: 6,
        fontSize: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        maxWidth: 260,
      }}
    >
      <div style={{ marginBottom: 6, fontWeight: 600 }}>
        Persentase Realisasi TKD
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 6 }}>
        {colors.map((c, i) => (
          <React.Fragment key={i}>
            <div
              style={{
                width: 20,
                height: 16,
                background: c,
                border: "1px solid #999",
              }}
            />
            <div>{autoLabels[i]}%</div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/* Update info pill */
function UpdateInfo() {
  const sheetInfoUpdate =
    "https://docs.google.com/spreadsheets/d/1vTuEXERFF-i-QBj6Vse912Hkv3J5SWhblcPPRIKWuew/gviz/tq?tqx=out:csv&gid=2070513025";

  const [updatedData, setUpdatedData] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(sheetInfoUpdate, { signal: controller.signal });
        const text = await res.text();

        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const rows = (results.data || []).filter(
              (r) => r && Object.keys(r).length > 0
            );
            const fields = results.meta?.fields || [];
            if (fields.length < 2) {
              setUpdatedData("");
              return;
            }
            const colBKey = fields[1];
            for (let i = rows.length - 1; i >= 0; i--) {
              const v = rows[i]?.[colBKey];
              if (v != null && String(v).trim() !== "") {
                setUpdatedData(String(v).trim());
                return;
              }
            }
            setUpdatedData("");
          },
          error: (err) => setError(err),
        });
      } catch (e) {
        if (e.name !== "AbortError") setError(e);
      }
    })();
    return () => controller.abort();
  }, []);

  return (
    <div
      style={{
        position: "absolute",
        left: 10,
        bottom: 10,
        zIndex: 1000,
        background: "white",
        padding: "8px 10px",
        border: "1px solid #ccc",
        borderRadius: 6,
        fontSize: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
      }}
    >
      <div style={{ marginBottom: 6, fontWeight: 600 }}>
        {error
          ? "Tanggal pembaruan tidak tersedia"
          : updatedData
          ? `Data per ${updatedData}`
          : "Memuat tanggal pembaruan..."}
      </div>
    </div>
  );
}

/* Info box */
function InfoBox({ hovered }) {
  const fmt = new Intl.NumberFormat("id-ID");
  const fmtCurrency = (n) =>
    Number.isFinite(n) ? `Rp${fmt.format(Math.round(n))}` : "—";
  const fmtPercent = (n) =>
    Number.isFinite(n) ? `${fmt.format(Math.round(n))}%` : "—";

  return (
    <div
      style={{
        position: "absolute",
        top: 10,
        right: 10,
        zIndex: 1000,
        background: "white",
        padding: "8px 12px",
        border: "1px solid #ccc",
        borderRadius: 6,
        fontSize: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        minWidth: 240,
      }}
    >
      <div>
        <b>Pemda :</b>
        <br />
        {hovered?.alias ?? "—"}
      </div>
      <hr />
      <div style={{ paddingTop: 10 }}>
        <b>Pagu :</b>
        <br />
        {fmtCurrency(hovered?.pagu)}
      </div>
      <div>
        <b>Realisasi :</b>
        <br />
        {fmtCurrency(hovered?.realisasi)}
      </div>
      <div>
        <b>Persentase :</b>
        <br />
        {fmtPercent(hovered?.value)}
      </div>
    </div>
  );
}

export default function ChoroplethMap({
  geojson, // FeatureCollection; features should have `id` that matches your data.id
  // data item: { id, alias, value(=percent 0–100), pagu?, realisasi? }
  data,

  useFixedRange = true,
  colors = FIXED_COLORS,
  breaksMode = "equal",

  maxZoom = 12,
  minZoomFixed = null,
  boundsPadding = 0.15,
  lockDragToData = true,
  maxBoundsViscosity = 1.0,
  zoomOutFactor = 2.15,

  height = 600,
  background = "#e9edf5",
}) {
  const layerRef = useRef();
  const [hovered, setHovered] = useState(null);

  // NEW: label visibility toggle (default hidden)
  const [showLabels, setShowLabels] = useState(false);

  // formatters
  const fmt = useMemo(() => new Intl.NumberFormat("id-ID"), []);
  const fmtCurrency = (n) =>
    Number.isFinite(n) ? `Rp${fmt.format(Math.round(n))}` : "—";
  const fmtPercent = (n) =>
    Number.isFinite(n) ? `${fmt.format(Math.round(n))}%` : "—";

  // lookups + breaks
  const { valueById, aliasById, paguById, realisasiById, breaks } =
    useMemo(() => {
      const vMap = new Map();
      const aMap = new Map();
      const pMap = new Map();
      const rMap = new Map();

      for (const d of data || []) {
        if (!d || d.id == null) continue;
        const id = String(d.id).trim();
        vMap.set(id, Number(d.value));
        aMap.set(id, d.alias || id);
        if (d.pagu != null) pMap.set(id, Number(d.pagu));
        if (d.realisasi != null) rMap.set(id, Number(d.realisasi));
      }

      let brks;
      if (useFixedRange) {
        brks = FIXED_BREAKS;
      } else {
        const vals = [...vMap.values()].filter(Number.isFinite);
        const min = vals.length ? Math.min(...vals) : 0;
        const max = vals.length ? Math.max(...vals) : 1;
        const steps = colors.length;

        if (breaksMode === "quantile" && vals.length >= steps) {
          const sorted = [...vals].sort((a, b) => a - b);
          const q = (p) => {
            const idx = (sorted.length - 1) * p;
            const lo = Math.floor(idx),
              hi = Math.ceil(idx);
            if (lo === hi) return sorted[lo];
            return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
          };
          brks = Array.from({ length: steps + 1 }, (_, i) =>
            Math.round(q(i / steps))
          );
          brks[0] = min;
          brks[brks.length - 1] = max;
        } else {
          brks = Array.from({ length: steps + 1 }, (_, i) =>
            Math.round(min + (i * (max - min)) / steps)
          );
        }
      }

      return {
        valueById: vMap,
        aliasById: aMap,
        paguById: pMap,
        realisasiById: rMap,
        breaks: brks,
      };
    }, [data, colors, breaksMode, useFixedRange]);

  // color chooser
  const colorFor = (v) => {
    if (!Number.isFinite(v)) return "#EEE";
    const x = Math.max(
      FIXED_BREAKS[0],
      Math.min(FIXED_BREAKS[FIXED_BREAKS.length - 1], v)
    );
    for (let i = 0; i < breaks.length - 1; i++) {
      const lo = breaks[i];
      const hi = breaks[i + 1];
      const inFirstBin = i === 0 ? x >= lo && x <= hi : x > lo && x <= hi;
      if (inFirstBin) return colors[i];
    }
    return colors[colors.length - 1];
  };

  const style = (feature) => {
    const key = String(feature.id ?? "").trim();
    const v = valueById.get(key);
    return {
      fillColor: colorFor(v),
      weight: 1,
      opacity: 1,
      color: "#555",
      fillOpacity: 0.85,
    };
  };

  // Bind popup and hover events. We DO NOT bind permanent tooltips here anymore.
  const onEachFeature = (feature, layer) => {
    const key = String(feature.id ?? "").trim();
    const v = valueById.get(key);
    const a = aliasById.get(key);
    const p = paguById.get(key);
    const r = realisasiById.get(key);

    layer.on({
      mouseover: (e) => {
        const l = e.target;
        l.setStyle({ weight: 2, color: "#222", fillOpacity: 0.95 });
        setHovered({
          id: key,
          alias: a || key,
          value: v,
          pagu: p,
          realisasi: r,
        });
      },
      mouseout: (e) => {
        layerRef.current?.resetStyle(e.target);
        setHovered(null);
      },
    });

    layer.bindPopup(
      `<div style="font-size:12px; line-height:1.4;">
         <div style="font-weight:600; margin-bottom:4px;">${a || key}</div>
         <div><b>Pagu:</b> ${fmtCurrency(p)}</div>
         <div><b>Realisasi:</b> ${fmtCurrency(r)}</div>
         <div><b>Persentase:</b> ${fmtPercent(v)}</div>
       </div>`
    );
  };

  // NEW: toggle permanent labels by (un)binding tooltips on all layers
  useEffect(() => {
    const group = layerRef.current;
    if (!group) return;

    group.eachLayer((lyr) => {
      // Ensure it's a vector layer with feature id
      const id = String(lyr?.feature?.id ?? "").trim();
      if (!id) return;

      // Always unbind first to avoid duplicates
      if (lyr.unbindTooltip) lyr.unbindTooltip();

      if (showLabels) {
        const a = aliasById.get(id) || id;
        const v = valueById.get(id);
        lyr.bindTooltip(
          `${a}<br/><span style="opacity:.8">${fmtPercent(v)}</span>`,
          { permanent: true, direction: "center", className: "map-label" }
        );
      }
    });
  }, [showLabels, aliasById, valueById, fmtPercent]);

  return (
    <div style={{ position: "relative" }}>
      {/* Toggle button */}
      <div
        style={{
          position: "absolute",
          left: 10,
          bottom: 60,
          zIndex: 1100,
          background: "white",
          border: "1px solid #ccc",
          borderRadius: 6,
          padding: "6px 10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <label style={{ fontSize: 12, fontWeight: 600 }}>Label</label>
        <button
          onClick={() => setShowLabels((s) => !s)}
          style={{
            fontSize: 12,
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #bbb",
            background: showLabels ? "#1a73e8" : "#f5f5f5",
            color: showLabels ? "#fff" : "#222",
            cursor: "pointer",
          }}
          title={showLabels ? "Hide labels" : "Show labels"}
        >
          {showLabels ? "Sembunyikan" : "Tampilkan"}
        </button>
      </div>

      <InfoBox hovered={hovered} />
      <UpdateInfo />
      <Legend breaks={breaks} colors={colors} />

      <MapContainer
        style={{ height, width: "100%", background }}
        zoomSnap={0.25}
        zoomDelta={0.5}
        scrollWheelZoom
        worldCopyJump={false}
        inertia={true}
      >
        <FitToGeoJSON
          geojson={geojson}
          maxZoom={maxZoom}
          minZoomFixed={minZoomFixed}
          boundsPadding={boundsPadding}
          lockDragToData={lockDragToData}
          maxBoundsViscosity={maxBoundsViscosity}
          zoomOutFactor={zoomOutFactor}
        />
        <GeoJSON
          ref={layerRef}
          data={geojson}
          style={style}
          onEachFeature={onEachFeature}
        />
      </MapContainer>
    </div>
  );
}
